/**
 * Foothill College brand colors (W52 Worker F, S3 — theme).
 *
 * Sources, two first-party, which DISAGREE on the exact red (doc 51 S0-7):
 *   - Brand guide foothill.edu/marketing/pdf/2026-2027-brand-guide-tagged.pdf (read
 *     2026-10-12): Foothill Red PMS 187 #A61E2F, Foothill Yellow PMS 123 #FFC82E,
 *     Cream #F2E7D1, Cool Gray #D0D1CE; tertiary #E57200 / #4495D1 / #58B947.
 *   - Catalog CSS (catalog.foothill.edu/css/styles.css + custom.css): reds #A6343A,
 *     #AD1F23, #912D32, #85292E.
 *   The guide is the authority for the primary; the catalog's darkest red #85292E is the
 *   white-text `-strong` tier (first-party, not invented).
 *
 * Contrast (WCAG, measured 2026-10-12 against the token surfaces in index.css):
 *   Foothill Red #A61E2F: 7.37:1 on the card #FFFFFF, 6.55:1 on paper #F5F1EB; white
 *     text on it 7.37:1. `-strong` #85292E: white text 8.92:1.
 *   Foothill Yellow #FFC82E fails as TEXT on light surfaces, and `text-accent` renders at
 *     12 px in the audit (override badge / pill / "via override" label), so 4.5:1 applies.
 *     The accent TOKEN is Foothill Yellow's own hue darkened until it clears 4.5 on BOTH
 *     light surfaces: #8D6800 = 4.54:1 on paper, 5.11:1 on the card. On the dark surfaces
 *     it measures 3.60:1 (#161412) / 3.32:1 (#1F1C19) — the 3:1 floor only: the theme
 *     system has no dark-mode accent token (doc 51 S3-6; De Anza and SDSU record the same).
 *     The bright yellow lives in the -200/-300 tints for fills.
 *
 * Brand mark (components/ui/BrandMark.tsx needs every front face >= 50 RGB from every
 * other institution's): the red space is TAKEN — Foothill Red is 5.1 from SDSU's light
 * front (166 25 46) and 38.3 from De Anza's dark front, and Foothill Yellow is 35 from
 * Montgomery College's Aspire Gold (251 169 62, both modes, W52 Worker M). So the mark
 * uses the gold/cream family, as Merced College's mark uses its accent:
 *   light front = the accent text tier 141 104 0 (min distance 66.6, Merced College);
 *   dark front  = Foothill Cream 242 231 209, a published brand color (min 143.9, UMD).
 *   Measured against umd, sjsu, sdsu, ucmerced, deanza, merced-college and
 *   montgomery-college (the last read from feature/onboard-montgomery-college).
 * Ramps are HSL ladders pinned at the brand values (Foothill publishes no extended
 * palette). Text-only branding, no logo asset embedded.
 */
import type { ThemeColors } from '../types';

export const foothillColors: ThemeColors = {
  // Primary — Foothill Red (#A61E2F = rgb(166, 30, 47)); -strong = catalog dark red #85292E.
  '--color-primary':     '166 30 47',
  '--color-primary-strong': '133 41 46',
  '--color-primary-50': '253 242 243',
  '--color-primary-100': '244 191 198',
  '--color-primary-200': '234 141 153',
  '--color-primary-300': '225 90 107',
  '--color-primary-400': '216 40 62',
  '--color-primary-500': '166 30 47',
  '--color-primary-600': '136 25 39',
  '--color-primary-700': '106 19 30',
  '--color-primary-800': '76 14 22',
  '--color-primary-900': '46 8 13',

  // Accent — Foothill Yellow hue, light-mode TEXT tier (#8D6800 = rgb(141, 104, 0)); see header.
  '--color-accent':     '141 104 0',
  '--color-accent-50': '255 251 240',
  '--color-accent-100': '255 232 169',
  '--color-accent-200': '255 214 98',
  '--color-accent-300': '255 195 27',
  '--color-accent-400': '212 156 0',
  '--color-accent-500': '141 104 0',
  '--color-accent-600': '116 85 0',
  '--color-accent-700': '90 67 0',
  '--color-accent-800': '65 48 0',
  '--color-accent-900': '39 29 0',

  // Brand-mark faces. Light: bronze front, two darker oblique steps. Dark: Foothill Cream
  // front, two warmer/darker steps. Distances in the header.
  '--color-mark-front': '141 104 0',
  '--color-mark-top':   '112 82 0',
  '--color-mark-side':  '84 62 0',
  '--color-mark-front-dm': '242 231 209',
  '--color-mark-top-dm':   '214 196 160',
  '--color-mark-side-dm':  '176 158 124',
};
